
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Line,
  ComposedChart
} from 'recharts';
import { ChartPie, DollarSign, ArrowUpRight, TrendingUp } from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import { RevenueHistoryItem } from '@/hooks/useResellerPlanData';
import { useMasterLicenseSettings } from '@/contexts/MasterLicenseSettingsContext';
import { RevenueSplit } from '@/hooks/license/resellerPlans/types';

interface ResellerRevenueChartProps {
  revenueHistory: RevenueHistoryItem[];
}

const ResellerRevenueChart: React.FC<ResellerRevenueChartProps> = ({ revenueHistory }) => {
  const [hoveredMonth, setHoveredMonth] = useState<string | null>(null);
  const { licenseSettings } = useMasterLicenseSettings();
  const [processedData, setProcessedData] = useState<RevenueHistoryItem[]>([]);
  
  // Format month labels for display
  const formatMonthLabel = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short' });
  };

  // Apply revenue splits based on master license settings
  useEffect(() => {
    if (!revenueHistory.length || !licenseSettings.revenueSplits?.length) return;
    
    // Get active revenue splits
    const activeSplits = [...(licenseSettings.revenueSplits || [])]
      .filter(split => split.isActive)
      .sort((a, b) => b.volumeThreshold - a.volumeThreshold); // Sort descending by threshold
    
    // Apply splits to each month's revenue
    const updatedHistory = revenueHistory.map(item => {
      // Find applicable split tier based on total revenue
      const applicableSplit = activeSplits.find(split => 
        item.totalRevenue >= split.volumeThreshold
      ) || activeSplits[activeSplits.length - 1]; // Fallback to base tier
      
      if (!applicableSplit) return item; // Keep original if no splits are found
      
      const masterLicensePercentage = applicableSplit.masterLicensePercentage / 100;
      const resellerPercentage = applicableSplit.resellerPercentage / 100;
      
      return {
        ...item,
        masterLicenseAmount: Math.round(item.totalRevenue * masterLicensePercentage),
        resellerAmount: Math.round(item.totalRevenue * resellerPercentage),
        splitPercentage: applicableSplit.resellerPercentage
      };
    });
    
    setProcessedData(updatedHistory);
  }, [revenueHistory, licenseSettings.revenueSplits]);
  
  // Prepare data with formatted month labels
  const chartData = processedData.length > 0 
    ? processedData.map(item => ({
        ...item,
        monthLabel: formatMonthLabel(item.month)
      }))
    : revenueHistory.map(item => ({
        ...item,
        monthLabel: formatMonthLabel(item.month)
      }));
  
  // Calculate total revenue
  const totalRevenue = revenueHistory.reduce((sum, item) => sum + item.totalRevenue, 0);
  
  // Calculate total reseller share based on processed data
  const totalResellerShare = processedData.length > 0
    ? processedData.reduce((sum, item) => sum + item.resellerAmount, 0)
    : revenueHistory.reduce((sum, item) => sum + item.resellerAmount, 0);
  
  // Calculate growth percentage (comparing last month to first month)
  const calculateGrowth = () => {
    if (revenueHistory.length < 2) return 0;
    
    const firstMonth = revenueHistory[0].totalRevenue;
    const lastMonth = revenueHistory[revenueHistory.length - 1].totalRevenue;
    
    return ((lastMonth - firstMonth) / firstMonth) * 100;
  };
  
  const growthPercentage = calculateGrowth();
  const isPositiveGrowth = growthPercentage >= 0;

  // Custom tooltip formatter
  const customTooltipFormatter = (value: number, name: string) => {
    switch (name) {
      case 'masterLicenseAmount':
        return [`$${value.toLocaleString()}`, 'Master License'];
      case 'resellerAmount':
        return [`$${value.toLocaleString()}`, 'Reseller Share'];
      case 'totalRevenue':
        return [`$${value.toLocaleString()}`, 'Total Revenue'];
      default:
        return [value, name];
    }
  };

  return (
    <Card className="col-span-3 lg:col-span-2">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center">
          <ChartPie className="mr-2 h-5 w-5 text-indigo-600" />
          <CardTitle className="text-indigo-700">Monthly Revenue Share</CardTitle>
        </div>
        <div className="flex flex-col items-end">
          <div className="flex items-center text-sm text-muted-foreground mb-1">
            <DollarSign className="mr-1 h-4 w-4" />
            Last 6 months
          </div>
          <div className={`flex items-center text-sm ${isPositiveGrowth ? 'text-green-600' : 'text-red-600'}`}>
            {isPositiveGrowth ? (
              <ArrowUpRight className="mr-1 h-4 w-4" />
            ) : (
              <TrendingUp className="mr-1 h-4 w-4" />
            )}
            <span>
              {isPositiveGrowth ? '+' : ''}{growthPercentage.toFixed(1)}% growth
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-6">
            <div>
              <p className="text-xs text-muted-foreground">Total Revenue</p>
              <p className="text-xl font-bold">${totalRevenue.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Reseller Share</p>
              <p className="text-xl font-bold text-green-600">
                ${totalResellerShare.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            {hoveredMonth ? 
              `Viewing: ${hoveredMonth}` : 
              'Hover over chart for details'}
          </div>
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 10,
              }}
              onMouseMove={(data) => {
                if (data.activeLabel) {
                  setHoveredMonth(data.activeLabel);
                }
              }}
              onMouseLeave={() => {
                setHoveredMonth(null);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthLabel" />
              <YAxis 
                yAxisId="left"
                orientation="left"
                tickFormatter={(value) => `$${value}`}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip 
                formatter={(value: number, name: string) => customTooltipFormatter(value, name)}
                contentStyle={{ 
                  backgroundColor: '#fff',
                  borderRadius: '6px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #f0f0f0'
                }}
              />
              <Legend />
              <Bar 
                dataKey="masterLicenseAmount" 
                name="Master License" 
                stackId="a" 
                fill="#6366f1" 
                yAxisId="left"
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="resellerAmount" 
                name="Reseller Share" 
                stackId="a" 
                fill="#22c55e"
                yAxisId="left"
                radius={[4, 4, 0, 0]}
              />
              <Line 
                type="monotone" 
                dataKey="totalRevenue" 
                name="Total Revenue" 
                stroke="#f59e0b" 
                strokeWidth={2}
                yAxisId="right"
                dot={{ r: 4, fill: '#f59e0b' }}
                activeDot={{ r: 6, fill: '#f59e0b', stroke: '#fff' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default ResellerRevenueChart;
